import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  FormGroup,
  Label,
  Badge,
  Alert,
  Spinner
} from 'reactstrap';
import axios from 'axios';

const MappingEditor = ({ mappingSet, isOpen, toggle, onSaved }) => {
  const [entries, setEntries] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newEntry, setNewEntry] = useState(getEmptyEntry());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  useEffect(() => {
    if (mappingSet) {
      setEntries(mappingSet.mappings || []);
    }
  }, [mappingSet]);

  function getEmptyEntry() {
    return {
      fieldname: '',
      aliases: [],
      scan_group: '',
      nist_controls: [],
      ssp_sections: [],
      severity_bias: 'medium',
      audit_desc: ''
    };
  }

  const handleAddNew = () => {
    setAddingNew(true);
    setNewEntry(getEmptyEntry());
    setEditingIndex(null);
  };

  const handleCancelAdd = () => {
    setAddingNew(false);
    setNewEntry(getEmptyEntry());
  };

  const handleSaveNew = async () => {
    if (!newEntry.fieldname.trim()) {
      setError('Field name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await axios.post(
        `${baseURL}/ssp-studio/mappings/${mappingSet._id}/entries`,
        newEntry,
        { withCredentials: true }
      );

      if (response.data.success) {
        setEntries(response.data.data.mappings);
        setSuccess('Mapping entry added successfully');
        setAddingNew(false);
        setNewEntry(getEmptyEntry());
        setTimeout(() => setSuccess(null), 3000);
        if (onSaved) onSaved();
      } else {
        setError(response.data.message || 'Failed to add entry');
      }
    } catch (err) {
      console.error('Error adding entry:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setAddingNew(false);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const handleSaveEdit = async (index) => {
    const entry = entries[index];

    try {
      setSaving(true);
      setError(null);

      const response = await axios.put(
        `${baseURL}/ssp-studio/mappings/${mappingSet._id}/entries/${entry.fieldname}`,
        entry,
        { withCredentials: true }
      );

      if (response.data.success) {
        setEntries(response.data.data.mappings);
        setSuccess('Mapping entry updated successfully');
        setEditingIndex(null);
        setTimeout(() => setSuccess(null), 3000);
        if (onSaved) onSaved();
      } else {
        setError(response.data.message || 'Failed to update entry');
      }
    } catch (err) {
      console.error('Error updating entry:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete mapping for "${entry.fieldname}"?`)) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await axios.delete(
        `${baseURL}/ssp-studio/mappings/${mappingSet._id}/entries/${entry.fieldname}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        setEntries(response.data.data.mappings);
        setSuccess('Mapping entry deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
        if (onSaved) onSaved();
      } else {
        setError(response.data.message || 'Failed to delete entry');
      }
    } catch (err) {
      console.error('Error deleting entry:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateEntry = (index, field, value) => {
    const updated = [...entries];
    updated[index][field] = value;
    setEntries(updated);
  };

  const updateNewEntry = (field, value) => {
    setNewEntry({ ...newEntry, [field]: value });
  };

  const parseArrayInput = (value) => {
    if (!value) return [];
    return value.split(',').map(v => v.trim()).filter(v => v);
  };

  const renderEditableRow = (entry, index, isNew = false) => {
    const isEditing = isNew || editingIndex === index;

    return (
      <tr key={isNew ? 'new' : index}>
        <td>
          {isNew ? (
            <Input
              type="text"
              size="sm"
              value={entry.fieldname}
              onChange={(e) => updateNewEntry('fieldname', e.target.value)}
              placeholder="e.g., ssl_version"
            />
          ) : (
            <code>{entry.fieldname}</code>
          )}
        </td>
        <td>
          {isEditing ? (
            <Input
              type="text"
              size="sm"
              value={Array.isArray(entry.aliases) ? entry.aliases.join(', ') : ''}
              onChange={(e) =>
                isNew
                  ? updateNewEntry('aliases', parseArrayInput(e.target.value))
                  : updateEntry(index, 'aliases', parseArrayInput(e.target.value))
              }
              placeholder="alias1, alias2"
            />
          ) : (
            entry.aliases && entry.aliases.length > 0 ? (
              entry.aliases.map((alias, i) => (
                <Badge key={i} color="info" className="me-1">
                  {alias}
                </Badge>
              ))
            ) : (
              <span className="text-muted">—</span>
            )
          )}
        </td>
        <td>
          {isEditing ? (
            <Input
              type="text"
              size="sm"
              value={entry.scan_group || ''}
              onChange={(e) =>
                isNew
                  ? updateNewEntry('scan_group', e.target.value)
                  : updateEntry(index, 'scan_group', e.target.value)
              }
              placeholder="e.g., Cryptography"
            />
          ) : (
            entry.scan_group || '—'
          )}
        </td>
        <td>
          {isEditing ? (
            <Input
              type="text"
              size="sm"
              value={Array.isArray(entry.nist_controls) ? entry.nist_controls.join(', ') : ''}
              onChange={(e) =>
                isNew
                  ? updateNewEntry('nist_controls', parseArrayInput(e.target.value))
                  : updateEntry(index, 'nist_controls', parseArrayInput(e.target.value))
              }
              placeholder="AC-1, SC-8"
            />
          ) : (
            entry.nist_controls && entry.nist_controls.length > 0 ? (
              entry.nist_controls.map((ctrl, i) => (
                <Badge key={i} color="secondary" className="me-1">
                  {ctrl}
                </Badge>
              ))
            ) : (
              <span className="text-muted">—</span>
            )
          )}
        </td>
        <td>
          {isEditing ? (
            <Input
              type="text"
              size="sm"
              value={Array.isArray(entry.ssp_sections) ? entry.ssp_sections.join(', ') : ''}
              onChange={(e) =>
                isNew
                  ? updateNewEntry('ssp_sections', parseArrayInput(e.target.value))
                  : updateEntry(index, 'ssp_sections', parseArrayInput(e.target.value))
              }
              placeholder="sec-1, sec-3"
            />
          ) : (
            entry.ssp_sections && entry.ssp_sections.length > 0 ? (
              entry.ssp_sections.join(', ')
            ) : (
              <span className="text-muted">—</span>
            )
          )}
        </td>
        <td>
          {isEditing ? (
            <Input
              type="select"
              size="sm"
              value={entry.severity_bias || 'medium'}
              onChange={(e) =>
                isNew
                  ? updateNewEntry('severity_bias', e.target.value)
                  : updateEntry(index, 'severity_bias', e.target.value)
              }
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </Input>
          ) : (
            <Badge
              color={
                entry.severity_bias === 'critical' ? 'danger' :
                entry.severity_bias === 'high' ? 'warning' :
                entry.severity_bias === 'medium' ? 'info' :
                'secondary'
              }
            >
              {entry.severity_bias || 'medium'}
            </Badge>
          )}
        </td>
        <td>
          {isNew ? (
            <div className="btn-group btn-group-sm">
              <Button
                color="success"
                size="sm"
                onClick={handleSaveNew}
                disabled={saving}
              >
                <i className="mdi mdi-check"></i>
              </Button>
              <Button
                color="secondary"
                size="sm"
                onClick={handleCancelAdd}
                disabled={saving}
              >
                <i className="mdi mdi-close"></i>
              </Button>
            </div>
          ) : isEditing ? (
            <div className="btn-group btn-group-sm">
              <Button
                color="success"
                size="sm"
                onClick={() => handleSaveEdit(index)}
                disabled={saving}
              >
                <i className="mdi mdi-check"></i>
              </Button>
              <Button
                color="secondary"
                size="sm"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                <i className="mdi mdi-close"></i>
              </Button>
            </div>
          ) : (
            <div className="btn-group btn-group-sm">
              <Button
                color="primary"
                size="sm"
                onClick={() => handleEdit(index)}
                disabled={saving || addingNew}
              >
                <i className="mdi mdi-pencil"></i>
              </Button>
              <Button
                color="danger"
                size="sm"
                onClick={() => handleDelete(entry)}
                disabled={saving || addingNew || editingIndex !== null}
              >
                <i className="mdi mdi-delete"></i>
              </Button>
            </div>
          )}
        </td>
      </tr>
    );
  };

  if (!mappingSet) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" style={{ maxWidth: '95vw' }}>
      <ModalHeader toggle={toggle}>
        <i className="mdi mdi-pencil-box-outline me-2"></i>
        Edit Mapping: {mappingSet.name}
        <Badge color="light" className="ms-2">
          {entries.length} entries
        </Badge>
      </ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="success" toggle={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <div className="mb-3">
          <Button
            color="success"
            size="sm"
            onClick={handleAddNew}
            disabled={addingNew || editingIndex !== null || saving}
          >
            <i className="mdi mdi-plus me-1"></i>
            Add New Mapping Entry
          </Button>
        </div>

        <Table bordered hover size="sm" responsive>
          <thead className="table-light">
            <tr>
              <th style={{ width: '15%' }}>Field Name</th>
              <th style={{ width: '15%' }}>Aliases</th>
              <th style={{ width: '12%' }}>Scan Group</th>
              <th style={{ width: '20%' }}>NIST Controls</th>
              <th style={{ width: '15%' }}>SSP Sections</th>
              <th style={{ width: '10%' }}>Severity Bias</th>
              <th style={{ width: '13%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {addingNew && renderEditableRow(newEntry, -1, true)}

            {entries.map((entry, index) => renderEditableRow(entry, index))}

            {entries.length === 0 && !addingNew && (
              <tr>
                <td colSpan="7" className="text-center text-muted py-4">
                  <i className="mdi mdi-information-outline me-2"></i>
                  No mapping entries yet. Click "Add New Mapping Entry" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {saving && (
          <div className="text-center py-3">
            <Spinner color="primary" size="sm" className="me-2" />
            <span className="text-muted">Saving changes...</span>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="text-muted small me-auto">
          <i className="mdi mdi-information-outline me-1"></i>
          Use comma-separated values for arrays (e.g., "AC-1, SC-8")
        </div>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MappingEditor;
